import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  Divider,
  message,
  Radio,
  Switch,
  notification,
  Button,
  Tooltip,
  Collapse,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  ScanOutlined,
} from '@ant-design/icons';
import moment from 'moment';

import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import POSContext from '../POSContext';
import useDictionaries from '../../../hooks/useDictionaries';

import {
  getUserTypesByIds,
  getUserTypes,
} from '../../../services/taxonomies/user-types';
import { getPosDocTypes } from '../../../services/taxonomies/doc-types';
import { getUserFilesById } from '../../../services/admin/users';
import {
  cancelOrder,
  getEduInfo,
  getAvailableLayouts,
  setOrderAccount,
} from '../../../services/pos/pos';
import {
  SERVICE_URI as FILE_SERVICE_URI,
  BASE_URI as FILE_BASE_URI,
  downloadFiles,
  uploadScan,
  getFiles,
} from '../../../services/files';

import Span from '../Span';
import POSButton from '../POSButton';
import WebCamImage from '../WebCamImage';
import { getInstitutions } from '../../../services/taxonomies/institutions';
import { getSubdivisions } from '../../../services/taxonomies/subdivisions';
import useFormErrors from '../../../hooks/useFormErrors';
import DatePickerLocale from '../../DatePickerLocale';
import UserTypesFields from '../UserTypesFields';
import PhoneInput from '../components/PhoneInput';
import FileUploader from '../../FileUploader';
import { scan } from '../../../helpers/scanner';
import { EduInfoCard } from '../components/EduInfoCard';
import {
  Environments,
  PriorityCountries,
  EnvToPriorityDistrictMap,
} from '../../../helpers/constants';
import { MIFARE_TYPES } from '../../../pages/orders/constants';
import { getCountries } from '../../../services/taxonomies/countires';

const getDictionaries = (ids) => ({
  userTypes: ids && ids.length > 0 ? getUserTypesByIds(ids) : getUserTypes,
  docTypes: getPosDocTypes,
  countries: () =>
    getCountries({ pageSize: 200, criterias: { deleted: 'false' } }),
});

const populateInstitutions = (userTypeId, userTypes, setInstitutions) => {
  const insts =
    userTypes.find((ut) => ut.id === userTypeId)?.institutions || [];
  if (insts.length) {
    getInstitutions({ pageSize: 2000, criterias: { ids: insts } }).then(
      (res) => {
        setInstitutions(res.content);
      },
    );
  } else {
    setInstitutions([]);
  }
};

const populateSubdivisions = (institutionId, institutions, setSubdivisions) => {
  const subdivisions =
    institutions.find((i) => i.id === institutionId)?.subdivisions || [];
  if (subdivisions.length) {
    getSubdivisions({ pageSize: 100, criterias: { ids: subdivisions } }).then(
      (res) => {
        setSubdivisions(res.content);
      },
    );
  } else {
    setSubdivisions([]);
  }
};

const CustomizeCard = () => {
  const { t } = useTranslation();
  const { shift, order, setOrder } = useContext(POSContext);
  const dictionaries = useMemo(
    () =>
      getDictionaries([
        ...new Set(
          (order.products || [])
            .flatMap((item) => item.payload)
            .map((item) => item.userTypeId)
            .filter((item) => item),
        ),
      ]),
    [order],
  );

  const phoneRegex = useMemo(() => /^(?:\+?\d{10,14}|[-])$/, []);

  const [{ userTypes, docTypes, countries }] = useDictionaries(dictionaries);

  const { account, card, products } = order || {};
  const { userProfileTypeId, institutionId, subdivisionId, userId } =
    account || {};
  const { cardPhysicalId } = card || null;
  const isExternalCard =
    cardPhysicalId !== undefined && cardPhysicalId !== null;
  const isNominal = !products.find(
    (p) => p.code === MIFARE_TYPES.MIFARE_1K_NENOMINAL,
  );
  const environment = useMemo(() => window._env_.ENVIROMENT, []);
  const isEduEnabledString = useMemo(() => window._env_.EDU_ENABLE, []);
  const isEduEnabled = isEduEnabledString === 'true';
  const [eduInfo, setEduInfo] = useState({});

  const [form] = Form.useForm();
  const [errors, setErrors] = useState(null);
  useFormErrors(form, errors);

  const [layouts, setLayouts] = useState([]);
  const [institutions, setInstitutions] = useState([]);

  const [subdivisions, setSubdivisions] = useState([]);

  const scanEnableValue = window._env_.SCAN_ENABLE;
  const scanEnable = scanEnableValue === 'true';
  const identityCard = Form.useWatch('identityCard', form);

  const [userFiles, setUserFiles] = useState([]);

  const priorityDistrictName = useMemo(
    () => EnvToPriorityDistrictMap[environment] || null,
    [environment],
  );

  const countryId = Form.useWatch(['address2', 'countryId'], form);

  const countryOptions = useMemo(() => {
    const rawCountries = countries?.content || [];

    return [...rawCountries]
      .sort((a, b) => {
        if (a.name === PriorityCountries.ROMANIA) return -1;
        if (b.name === PriorityCountries.ROMANIA) return 1;

        return a.name.localeCompare(b.name, 'ro');
      })
      .map((country) => ({
        label: country.name,
        value: country.id,
        disabled: country.deleted,
      }));
  }, [countries?.content]);

  const districtOptions = useMemo(() => {
    const districts =
      (countries?.content || []).find((c) => c.id === countryId)?.districts ||
      [];

    return [...districts]
      .sort((a, b) => {
        if (a.name === priorityDistrictName) return -1;
        if (b.name === priorityDistrictName) return 1;

        return a.name.localeCompare(b.name, 'ro');
      })
      .map((district) => ({
        label: district.name,
        value: district.id,
        disabled: district.deleted,
      }));
  }, [countries?.content, countryId, priorityDistrictName]);

  const viewUserFile = useCallback(
    (fileId) => {
      if (fileId) {
        downloadFiles(fileId);
      } else {
        notification.error({
          message: t('errors.downloadError'),
        });
      }
    },
    [t],
  );

  useEffect(() => {
    if (userId) {
      getUserFilesById(userId)
        .then((res) => {
          if (res.length > 0) {
            getFiles(res.map((item) => item.id)).then((res2) => {
              // merge 2 arrays by fileId
              const mergedMap = [...res, ...res2].reduce((acc, obj) => {
                acc[obj.id] = { ...acc[obj.id], ...obj };
                return acc;
              }, {});
              const mergedArray = Object.values(mergedMap);

              // group files by docTypeId and the the latest updatedDate
              const groupedDocs = Object.values(
                mergedArray.reduce((acc, doc) => {
                  if (
                    !acc[doc.docTypeId] ||
                    new Date(doc.uploadDate) >
                      new Date(acc[doc.docTypeId].uploadDate)
                  ) {
                    acc[doc.docTypeId] = doc;
                  }
                  return acc;
                }, {}),
              );

              const usrFiles = groupedDocs.map((item) => {
                const dt = docTypes.content.find(
                  (it) => it.id === item.docTypeId,
                );
                return {
                  id: item.id,
                  docName: dt?.name || '',
                  fileName: item.name,
                  expirationDate: item?.expirationDate,
                  uploadDate: item?.uploadDate,
                  fileSize: item?.size,
                  docTypeId: item?.docTypeId,
                };
              });
              setUserFiles(usrFiles);
            });
          }
        })
        .catch((err) => console.error(err));
    }
  }, [userId, docTypes.content]);

  useEffect(() => {
    if (eduInfo?.lastName) {
      setEduInfo({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityCard]);

  useEffect(() => {
    if (userProfileTypeId) {
      populateInstitutions(
        userProfileTypeId,
        userTypes.content,
        setInstitutions,
      );
    }
  }, [userTypes.content, userProfileTypeId]);

  useEffect(() => {
    if (!isExternalCard) {
      getAvailableLayouts(shift, order)
        .then((c) => {
          const updatedLayouts = c.map((layout) => ({
            ...layout,
            disabled: false,
          }));
          setLayouts(updatedLayouts);
        })
        .catch((err) => {
          console.error(err);
          setLayouts([]);
        });
    }
  }, [shift, order, isExternalCard]);

  useEffect(() => {
    form.setFieldsValue({ layout: null });
    const notDisabledLayers = layouts.filter(
      (layout) => layout.disabled === false,
    );
    if (notDisabledLayers.length > 0) {
      const layoutId = notDisabledLayers.filter((_, idx) => idx === 0)[0]
        ?.fileId;
      form.setFieldsValue({ layout: layoutId });
    }
  }, [layouts, form]);

  useEffect(() => {
    const type = !userTypes.loading
      ? // eslint-disable-next-line eqeqeq
        userTypes.content.find((c) => c.id == userProfileTypeId)
      : {};
    const docs = type?.docTypes || [];

    const res = !docTypes.loading
      ? docTypes.content.filter((dt) => docs.includes(dt.id))
      : [];
    form.setFieldsValue({
      name: order.account?.userFullname?.trimStart().toUpperCase(),
      userTypeId: order.account?.userProfileTypeId,
      metaData1: order.account?.metaData1,
      metaData2: order.account?.metaData2,
      identityCard: order.account?.userIdentityCard,
      email: order.account?.userEmail,
      phoneNumber: order.account?.userPhone,
      address2: order.account?.address2 || {
        countryId: null,
        districtId: null,
        locality: null,
        street: null,
        number: null,
        building: null,
        apartment: null,
      },
      files: res
        .filter((r) => !r.deleted)
        .map((r) => ({
          hasExpirationDate: r.hasExpirationDate,
          dateChangeable: r.dateChangeable,
          durationType: r.durationType,
          expirationDate: r.expirationDate ? moment(r.expirationDate) : null,
          docTypeId: r.id,
          fileId: null,
          docTypeName: r.name,
          name: null,
          verified: false,
        })),
    });
  }, [form, order, userTypes, docTypes, userProfileTypeId]);

  const handlePhoneChange = useMemo(
    () =>
      debounce((phone) => {
        form.setFieldsValue({ phoneNumber: phone });
      }, 500),
    [form],
  );

  const handleClickEduInfo = useCallback(() => {
    getEduInfo(identityCard?.trim())
      .then((res) => {
        setEduInfo(res);
      })
      .catch((err) => {
        setEduInfo({ studyStatus: t(err.inner._) });
        message.error({
          content: t(err.inner._),
          key: 'pos',
          duration: 3,
          className: 'card-message',
        });
      });
  }, [t, identityCard]);

  const disabledDate = (current) => {
    // Can not select days before today and today
    return current < moment().add(-1, 'day');
  };

  useEffect(() => {
    form.setFieldsValue({
      institutionId,
      subdivisionId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (institutionId) {
      populateSubdivisions(institutionId, institutions, setSubdivisions);
    }
  }, [form, institutionId, institutions, setSubdivisions]);

  const handleChangeUserTypeId = useCallback(
    (value) => {
      // eslint-disable-next-line eqeqeq
      const type = userTypes.content.find((c) => c.id == value);
      const docs = type?.docTypes || [];

      if (value) {
        setSubdivisions([]);
        populateInstitutions(value, userTypes.content, setInstitutions);
      }

      const res = docTypes.content.filter((dt) => docs.includes(dt.id));
      form.setFieldsValue({
        institutionId: null,
        subdivisionId: null,
        metaData1: null,
        files: res
          .filter((r) => !r.deleted)
          .map((r) => ({
            hasExpirationDate: r.hasExpirationDate,
            dateChangeable: r.dateChangeable,
            durationType: r.durationType,
            docTypeId: r.id,
            fileId: null,
            docTypeName: r.name,
            name: null,
            expirationDate: r.expirationDate ? moment(r.expirationDate) : null,
            verified: false,
          })),
      });
    },
    [docTypes.content, userTypes.content, form],
  );

  const handleChangeInstitution = useCallback(
    (val) => {
      form.resetFields(['subdivisionId']);
      setSubdivisions([]);
      populateSubdivisions(val, institutions, setSubdivisions);
    },
    [institutions, form],
  );

  const handleCancelOrder = useCallback(() => {
    cancelOrder(shift, order)
      .then((res) => {
        setOrder(res);
      })
      .catch((err) => {
        console.error(err);
        message.error({
          content: t('entity.pos.errors.cancelOrderError'),
          key: 'pos',
          duration: 1,
          className: 'card-message',
        });
      });
  }, [shift, order, setOrder, t]);

  const processOrderAccount = useCallback(
    (value) => {
      let body = {};

      if (!isNominal) {
        const layout = form.getFieldValue('layout');
        const userType = userTypes?.content?.find(
          (ut) => ut.code === 'CALATOR',
        );

        body = {
          name: 'NENOMINAL',
          identityCard: '-',
          userTypeId: userType?.id,
          institutionId: userType?.institutions[0],
          isExternalCard,
          layout,
        };
      }

      return setOrderAccount(shift, order, isNominal ? value : body);
    },
    [userTypes, form, isExternalCard, isNominal, order, shift],
  );

  const viewFile = useCallback(
    (field) => {
      const file = form.getFieldsValue(['files'])?.files[field.name];
      const fileId = file?.fileId;
      if (fileId) {
        downloadFiles(fileId);
      } else {
        notification.error({
          message: t('errors.downloadError'),
        });
      }
    },
    [form, t],
  );

  const isBiggerThan5MB = (bytes) => {
    const MB = 1024 * 1024;
    return bytes > 5 * MB;
  };

  const scanFile = useCallback(
    (field) => {
      scan()
        .then(
          (resp) => (resp.ok ? resp.blob() : Promise.resolve(0)),
          () => Promise.resolve(0),
        )
        .then((scanData) => {
          if (!scanData) {
            notification.error({
              message: t('errors.uploadError'),
            });
            throw new Error('Scan failed or returned no data');
          }

          if (isBiggerThan5MB(scanData.size)) {
            notification.error({
              message: t('errors.maxScannedFileSize', { maxSize: 5 }),
            });
            throw new Error('File is too big');
          }

          const documentType = form.getFieldsValue(['files'])?.files[
            field.name
          ];
          return uploadScan([scanData], documentType?.docTypeName);
        })
        .then((fileResponse) => {
          return fileResponse.json();
        })
        .then((res) => {
          const doc = form.getFieldsValue(['files'])?.files[field.name];
          if (res && doc) {
            const updatedDoc = {
              ...doc,
              fileId: res[0]?.id,
              name: res[0]?.name,
              verified: true,
            };
            form.setFieldsValue({
              files: {
                ...form.getFieldValue('files'),
                [field.name]: updatedDoc,
              },
            });
          }
          notification.success({
            message: t('errors.uploadSuccessful'),
          });
        })
        .catch((error) => {
          console.error('Error in scan or upload:', error);
        });
    },
    [form, t],
  );

  const addFiles = useCallback(
    (files, field) => {
      const file = files[0];
      const doc = form.getFieldsValue(['files'])?.files[field.name];
      if (file && doc) {
        const updatedDoc = {
          ...doc,
          fileId: file?.id,
          name: file?.name,
          verified: true,
        };
        form.setFieldsValue({
          files: {
            ...form.getFieldValue('files'),
            [field.name]: updatedDoc,
          },
        });
      }
    },
    [form],
  );

  const markFileAsVerified = useCallback(
    (field) => {
      const doc = form.getFieldsValue(['files'])?.files[field.name];
      if (scanEnable) {
        let updatedDoc = null;
        if (doc.verified) {
          updatedDoc = {
            ...doc,
            verified: true,
          };
        } else {
          updatedDoc = {
            ...doc,
            name: null,
            verified: false,
          };
        }
        form.setFieldsValue({
          files: {
            ...form.getFieldValue('files'),
            [field.name]: updatedDoc,
          },
        });
      }
    },
    [form, scanEnable],
  );

  const handleOk = useCallback(() => {
    form.setFieldValue('isExternalCard', isExternalCard);

    form
      .validateFields()
      .then((value) => processOrderAccount(value))
      .then((res) => setOrder(res))
      .catch((err) => {
        if (err?.errorFields?.length) {
          form.setFields(
            err.errorFields.map(({ name, errors: fieldErrors }) => ({
              name,
              errors: fieldErrors,
            })),
          );

          form.scrollToField(err.errorFields[0].name);
          return;
        }

        setErrors({});
        console.error(err);

        message.error({
          content: err?.inner?._
            ? t(err.inner._)
            : t('entity.pos.errors.accountCreationFailed'),
          key: 'pos',
          duration: 1,
          className: 'card-message',
        });

        setErrors(err.inner || {});
      });
  }, [form, isExternalCard, processOrderAccount, setOrder, t]);
  return (
    <div className="pos">
      <div className="pos__wrapper">
        <Form form={form} layout="vertical">
          <Form.Item hidden name={['isExternalCard']} />
          <Row
            gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}
            style={{ display: isNominal ? 'flex' : 'block' }}
          >
            {isNominal && (
              <Col span={10}>
                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}>
                  <Col span={12}>
                    <Form.Item
                      name="name"
                      label={t('entity.pos.cardReplacement.fullName')}
                      normalize={(val) => val.toUpperCase()}
                      rules={[
                        {
                          required: isNominal,
                          message: t('errors.notEmpty'),
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="userTypeId"
                      label={t('entity.pos.cardReplacement.userType')}
                      rules={[
                        {
                          required: isNominal,
                          message: t('errors.notEmpty'),
                        },
                      ]}
                    >
                      <Select
                        // loading={userTypes.loading}
                        onChange={handleChangeUserTypeId}
                      >
                        {userTypes.content.map((option) => (
                          <Select.Option
                            value={option.id}
                            key={option.id}
                            disabled={option.disabled || option.deleted}
                          >
                            {option.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      name="institutionId"
                      label={t('entity.pos.cardReplacement.institution')}
                      rules={[
                        {
                          required: isNominal,
                          message: t('errors.notEmpty'),
                        },
                      ]}
                    >
                      <Select
                        onChange={handleChangeInstitution}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {institutions.map((i) => (
                          <Select.Option
                            key={i.id}
                            value={i.id}
                            disabled={i.disabled || i.deleted}
                          >
                            {i.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <UserTypesFields
                      subdivisions={subdivisions}
                      showColumns={
                        userTypes?.content.find(
                          (ut) => ut.id === form.getFieldValue(['userTypeId']),
                        )?.showColumns
                      }
                    />
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="foreignCitizen"
                      label={t('entity.pos.cardReplacement.foreignCitizen')}
                    >
                      <Switch
                        checkedChildren={<CheckOutlined />}
                        unCheckedChildren={<CloseOutlined />}
                      />
                    </Form.Item>
                    <Form.Item
                      name="identityCard"
                      label={t('entity.pos.cardReplacement.identityNumber')}
                      rules={[
                        {
                          required: isNominal,
                          message: t('errors.notEmpty'),
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="email"
                      label={t('entity.pos.cardReplacement.email')}
                      rules={[
                        {
                          type: 'email',
                          message: t('errors.incorrectField'),
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="phoneNumber"
                      label={t('entity.pos.cardReplacement.phoneNumber')}
                      rules={[
                        {
                          required: isNominal,
                          message: t('errors.notEmpty'),
                        },
                        {
                          validator: (_, value) => {
                            if (value && value.trim() === '-') {
                              return Promise.resolve();
                            }
                            if (value && phoneRegex.test(value)) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(
                                t('entity.pos.errors.invalidPhoneNumber'),
                              ),
                            );
                          },
                        },
                      ]}
                    >
                      <PhoneInput onChange={handlePhoneChange} />
                    </Form.Item>
                  </Col>
                </Row>

                <Collapse defaultActiveKey={['addressPanel']} ghost>
                  <Collapse.Panel
                    header={t('entity.pos.cardReplacement.address')}
                    key="addressPanel"
                    forceRender
                    style={{ padding: 0 }}
                  >
                    <Row gutter={8}>
                      <Col span={8}>
                        <Form.Item
                          name={['address2', 'countryId']}
                          label={t('entity.admin.user.address.country')}
                          rules={[
                            {
                              required: true,
                              message: t('errors.notEmpty'),
                            },
                          ]}
                        >
                          <Select
                            options={countryOptions}
                            loading={countries?.loading}
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                              option.label
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            onChange={(selectedCountryId) => {
                              const currentAddress2 =
                                form.getFieldValue('address2') || {};

                              form.setFieldsValue({
                                address2: {
                                  ...currentAddress2,
                                  countryId: selectedCountryId ?? null,
                                  districtId: null,
                                },
                              });
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          name={['address2', 'districtId']}
                          label={t('entity.admin.user.address.district')}
                          rules={[
                            {
                              required: true,
                              message: t('errors.notEmpty'),
                            },
                          ]}
                        >
                          <Select
                            options={districtOptions}
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                              option.label
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            disabled={!countryId}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item
                          name={['address2', 'locality']}
                          label={t('entity.admin.user.address.locality')}
                          rules={[
                            {
                              required: true,
                              whitespace: true,
                              message: t('errors.notEmpty'),
                            },
                          ]}
                        >
                          <Input maxLength={32} allowClear />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={8}>
                      <Col span={12}>
                        <Form.Item
                          name={['address2', 'street']}
                          label={t('entity.admin.user.address.street')}
                        >
                          <Input maxLength={32} allowClear />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          name={['address2', 'number']}
                          label={t('entity.admin.user.address.number')}
                        >
                          <Input maxLength={8} allowClear />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          name={['address2', 'building']}
                          label={t('entity.admin.user.address.building')}
                        >
                          <Input maxLength={8} allowClear />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          name={['address2', 'apartment']}
                          label={t('entity.admin.user.address.ap')}
                        >
                          <Input maxLength={8} allowClear />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Collapse.Panel>
                </Collapse>

                {userFiles.length > 0 && (
                  <>
                    <Row>
                      <h3>{t('entity.accountsDetails.oldUserFiles')}</h3>
                    </Row>
                    {userFiles.map((item) => (
                      <Row
                        key={item.id}
                        gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}
                        style={{ padding: '0.5rem 0' }}
                      >
                        <Col span={scanEnable ? 4 : 6}>
                          <Row align="middle" style={{ height: '100%' }}>
                            {item.docName}
                          </Row>
                        </Col>
                        <Col span={scanEnable ? 8 : 10}>
                          <Row>
                            {t('entity.pos.cardReplacement.expirationDate')}
                          </Row>
                          <Row>
                            <DatePickerLocale
                              disabled
                              format="DD.MM.YYYY"
                              value={
                                item.expirationDate
                                  ? moment(item.expirationDate)
                                  : item.expirationDate
                              }
                              style={{ width: '100%' }}
                            />
                          </Row>
                        </Col>
                        {scanEnable && (
                          <>
                            <Col span={8}>
                              <Row>
                                {t('entity.pos.cardReplacement.uploadDate')}
                              </Row>
                              <Row>
                                <DatePickerLocale
                                  disabled
                                  format="DD.MM.YYYY"
                                  value={
                                    item.uploadDate
                                      ? moment(item.uploadDate)
                                      : item.uploadDate
                                  }
                                  style={{ width: '100%' }}
                                />
                              </Row>
                            </Col>
                            <Col span={4}>
                              <Row justify="end">
                                <Tooltip
                                  title={item.fileName}
                                  placement="top"
                                  arrowPointAtCenter
                                >
                                  <span
                                    style={{
                                      display: 'inline-block',
                                      width: '100px',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      verticalAlign: 'middle',
                                    }}
                                  >
                                    {item.fileName}
                                  </span>
                                </Tooltip>
                                {item.fileSize > 0 && (
                                  <Button
                                    onClick={() => viewUserFile(item.id)}
                                    style={{ marginLeft: 8 }}
                                  >
                                    <EyeOutlined />
                                  </Button>
                                )}
                              </Row>
                            </Col>
                          </>
                        )}
                      </Row>
                    ))}
                    <Divider />
                    <Row>
                      <h3>{t('entity.accountsDetails.newUserFiles')}</h3>
                    </Row>
                  </>
                )}
                <Form.List name="files" dependencies={['files']}>
                  {(fields) => (
                    <div>
                      {fields.map((field) => (
                        <Row
                          gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}
                          key={field.key}
                        >
                          <Col span={5}>
                            <Form.Item
                              name={[field.name, 'docTypeName']}
                              fieldKey={[field.fieldKey, 'docTypeName']}
                            >
                              <Span />
                            </Form.Item>
                          </Col>
                          {!form.getFieldValue([
                            'files',
                            field.name,
                            'hasExpirationDate',
                          ]) && <Col span={scanEnable ? 7 : 10} />}

                          {form.getFieldValue([
                            'files',
                            field.name,
                            'hasExpirationDate',
                          ]) && (
                            <Col span={scanEnable ? 7 : 10}>
                              <Form.Item
                                {...field}
                                name={[field.name, 'expirationDate']}
                                label={t(
                                  'entity.pos.cardReplacement.expirationDate',
                                )}
                                fieldKey={[field.fieldKey, 'expirationDate']}
                              >
                                <DatePickerLocale
                                  disabled={
                                    form.getFieldValue([
                                      'files',
                                      field.name,
                                      'durationType',
                                    ]) &&
                                    !form.getFieldValue([
                                      'files',
                                      field.name,
                                      'dateChangeable',
                                    ])
                                  }
                                  format="DD.MM.YYYY"
                                  style={{ width: '100%' }}
                                  disabledDate={disabledDate}
                                />
                              </Form.Item>
                            </Col>
                          )}
                          <Col span={scanEnable ? 5 : 8}>
                            <Form.Item
                              {...field}
                              name={[field.name, 'verified']}
                              fieldKey={[field.fieldKey, 'verified']}
                              key={field.fieldKey}
                              label={t('entity.pos.cardReplacement.isVerified')}
                            >
                              {scanEnable ? (
                                <Switch
                                  checked={
                                    form.getFieldsValue(['files'])?.files[
                                      field.name
                                    ]?.verified
                                  }
                                  checkedChildren={<CheckOutlined />}
                                  unCheckedChildren={<CloseOutlined />}
                                  onChange={() => markFileAsVerified(field)}
                                />
                              ) : (
                                <Switch
                                  checkedChildren={<CheckOutlined />}
                                  unCheckedChildren={<CloseOutlined />}
                                />
                              )}
                            </Form.Item>
                          </Col>
                          {scanEnable && (
                            <>
                              <Col span={7}>
                                <Row>
                                  <Tooltip
                                    title={form.getFieldValue([
                                      'files',
                                      field.name,
                                      'name',
                                    ])}
                                    placement="top"
                                    arrowPointAtCenter
                                  >
                                    <span
                                      style={{
                                        display: 'inline-block',
                                        width: '100px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        verticalAlign: 'middle',
                                      }}
                                    >
                                      {form.getFieldValue([
                                        'files',
                                        field.name,
                                        'name',
                                      ])}
                                    </span>
                                  </Tooltip>
                                </Row>

                                <Row>
                                  <Button onClick={() => scanFile(field)}>
                                    <ScanOutlined />
                                  </Button>
                                  <FileUploader
                                    onUploadSuccess={(files) =>
                                      addFiles(files, field)
                                    }
                                    multiple={false}
                                    dragger={false}
                                  />
                                  <Button
                                    onClick={() => viewFile(field)}
                                    disabled={
                                      !form.getFieldValue([
                                        'files',
                                        field.name,
                                        'fileId',
                                      ])
                                    }
                                  >
                                    <EyeOutlined />
                                  </Button>
                                </Row>
                              </Col>
                            </>
                          )}
                        </Row>
                      ))}
                    </div>
                  )}
                </Form.List>
              </Col>
            )}
            {!isExternalCard && (
              <Col span={isNominal ? 14 : 24}>
                {/* Nested Row for Poza and Layout side by side */}
                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}>
                  {isNominal && (
                    <Col span={6}>
                      <Form.Item
                        name="photo"
                        label={t('entity.pos.cardReplacement.picture')}
                      >
                        <WebCamImage layout="default" />
                      </Form.Item>
                    </Col>
                  )}

                  <Col span={isNominal ? 18 : 24}>
                    <Divider>{t('entity.pos.cardReplacement.layout')}</Divider>
                    <Form.Item
                      name="layout"
                      label={t('entity.pos.cardReplacement.layout')}
                      rules={[
                        {
                          required: !isExternalCard && isNominal,
                          message: t('errors.notEmpty'),
                        },
                      ]}
                    >
                      <Radio.Group>
                        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}>
                          {layouts.map((layout) => (
                            <Col
                              span={40}
                              key={layout.cardId}
                              style={{ textAlign: 'center' }}
                            >
                              <Radio
                                value={layout.fileId}
                                disabled={layout.disabled}
                              >
                                <img
                                  style={{
                                    border: '1px dotted lightgray',
                                    margin: '10px',
                                  }}
                                  src={`${window._env_.API_BACKEND_URL}${FILE_SERVICE_URI}${FILE_BASE_URI}/${layout.fileId}?download`}
                                  width="100%"
                                  height="120rem"
                                  alt=""
                                  onError={() => {
                                    const updatedLayouts = layouts.map((l) =>
                                      l.fileId === layout.fileId
                                        ? { ...l, disabled: true }
                                        : l,
                                    );
                                    setLayouts(updatedLayouts);
                                    notification.error({
                                      message: t(
                                        'entity.pos.errors.missingCardTemplate',
                                      ),
                                    });
                                  }}
                                />
                              </Radio>
                            </Col>
                          ))}
                        </Row>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>

                {isEduEnabled && environment === Environments.PITESTI && (
                  <Row
                    gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}
                    style={{ marginTop: '1rem' }}
                  >
                    <Col span={12}>
                      <POSButton
                        title={t('entity.pos.eduInfo.eduInfo')}
                        onClick={handleClickEduInfo}
                        color="green"
                        disabled={
                          !identityCard?.trim() ||
                          identityCard.trim().length !== 13
                        }
                      />
                      <EduInfoCard eduInfo={eduInfo} />
                    </Col>
                  </Row>
                )}
              </Col>
            )}
          </Row>
        </Form>
      </div>

      <div className="pos__actions">
        <div>
          <POSButton
            title={t('entity.pos.actions.cancel')}
            color="red"
            size="small"
            onClick={handleCancelOrder}
          />
        </div>

        <div>
          <POSButton
            title={t('entity.pos.actions.next')}
            color="green"
            size="small"
            onClick={handleOk}
          />
        </div>
      </div>
    </div>
  );
};

export default CustomizeCard;
